/**
 * Motion table: what moves, turns, scales, fades, appears or disappears during the first seconds of play, sampled at a fixed rate on a
 * disposable copy. Local transforms are read, so a child carried by a moving parent has no row of its own: the parent does.
 */
import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';
import { Renderer } from '../../behaviors/renderer';
import { Text } from '../../behaviors/text';
import { makeBudget, withRun } from './runtime';

export interface MotionRow {
    node: string;
    kind: 'move' | 'turn' | 'scale' | 'fade' | 'show' | 'hide';
    /** Seconds of play. */
    start: number;
    end: number;
    /** move: metres from where it began; turn: degrees; scale: factor on the start size, furthest from 1; fade: opacity change, signed; show and hide: 1. */
    amount: number;
    /** It went past where it ended up by more than 3% (move, scale). */
    overshoot?: boolean;
}

export interface MotionGroup {
    parent: string;
    count: number;
    /** Median gap between consecutive starts, seconds; null when fewer than two start at different times. */
    staggerS: number | null;
    /** `sequential` and `reverse` follow the siblings' order; `simultaneous` starts within one sample; null for a single node. */
    order: 'sequential' | 'reverse' | 'simultaneous' | 'mixed' | null;
}

export interface MotionOptions {
    /** Seconds of play to watch. Default 3. */
    windowS?: number;
    /** Samples per second. Default 10. */
    hz?: number;
    /** Wall-clock cap for the call, milliseconds. Default 3000. When it runs out the rows seen so far come back with `truncated: true`. */
    timeBudgetMs?: number;
}

export interface MotionTable {
    rows: MotionRow[];
    /** When the last non-continuous motion ended, seconds; null when nothing settles (no motion, or only motion still going at the window's end). */
    settledAt: number | null;
    /** Nothing moved, turned, scaled, faded, appeared or disappeared. */
    idle: boolean;
    groups: MotionGroup[];
    /** The time budget ran out: only part of the window was played. */
    truncated?: true;
}

const MOVE_EPS_M = 1e-4;
const TURN_EPS_DEG = 0.05;
const SCALE_EPS = 1e-3;
const FADE_EPS = 1e-3;
const OVERSHOOT = 1.03;

interface Sample { pos: [number, number, number]; rot: Quaternion; scale: [number, number, number]; opacity: number; active: boolean }

function alphaOf(hex: unknown): number {
    return typeof hex === 'string' && /^#[0-9a-fA-F]{8}$/.test(hex) ? parseInt(hex.slice(7, 9), 16) / 255 : 1;
}

function opacityOf(so: SceneObject): number {
    const text = so.getBehavior(Text);
    if (text) return alphaOf(text._getResolvedData().color);
    const renderer = so.getBehavior(Renderer)?._getResolvedData().opacity;
    if (typeof renderer === 'number' && Number.isFinite(renderer)) return renderer;
    const mesh = so.getOwnedMeshes()[0];
    return mesh ? mesh.visibility : 1;
}

function sample(so: SceneObject): Sample {
    const t = so.transform;
    return {
        pos: [t.position.x, t.position.y, t.position.z],
        rot: (t.rotationQuaternion ?? Quaternion.FromEulerVector(t.rotation)).clone(),
        scale: [t.scaling.x, t.scaling.y, t.scaling.z],
        opacity: opacityOf(so),
        active: so.active,
    };
}

const dist = (a: readonly number[], b: readonly number[]): number => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
const angleDeg = (a: Quaternion, b: Quaternion): number => 2 * Math.acos(Math.min(1, Math.abs(Quaternion.Dot(a, b)))) * 180 / Math.PI;
const meanScale = (a: readonly number[], b: readonly number[]): number => {
    let sum = 0, n = 0;
    for (let i = 0; i < 3; i++) if (Math.abs(b[i]) > 1e-9) { sum += a[i] / b[i]; n++; }
    return n ? sum / n : 1;
};
const round = (v: number): number => Math.round(v * 1e4) / 1e4;

function median(values: number[]): number {
    const s = [...values].sort((a, b) => a - b);
    return s.length % 2 ? s[(s.length - 1) / 2] : (s[s.length / 2 - 1] + s[s.length / 2]) / 2;
}

function rowsFor(node: string, series: Sample[], dt: number): { rows: MotionRow[]; continuous: Set<MotionRow> } {
    const rows: MotionRow[] = [], continuous = new Set<MotionRow>();
    const last = series.length - 1;
    const first = series[0];
    // A run of "moving" intervals gives start (the sample it began from) and end (the last sample it was still moving into).
    const span = (moving: boolean[]): { start: number; end: number; atEnd: boolean } | null => {
        const a = moving.indexOf(true);
        if (a < 0) return null;
        const b = moving.lastIndexOf(true);
        return { start: round(a * dt), end: round((b + 1) * dt), atEnd: b === moving.length - 1 };
    };
    const push = (row: MotionRow, atEnd: boolean) => { rows.push(row); if (atEnd) continuous.add(row); };
    const intervals = (changed: (a: Sample, b: Sample) => boolean) => series.slice(1).map((s, i) => changed(series[i], s));

    const move = span(intervals((a, b) => dist(a.pos, b.pos) > MOVE_EPS_M));
    if (move) {
        const away = series.map(s => dist(s.pos, first.pos));
        const finalDist = away[last];
        const dir = finalDist > 1e-3 ? first.pos.map((p, i) => (series[last].pos[i] - p) / finalDist) : null;
        const along = dir ? Math.max(...series.map(s => s.pos.reduce((acc, p, i) => acc + (p - first.pos[i]) * dir[i], 0))) : 0;
        push({ node, kind: 'move', start: move.start, end: move.end, amount: round(Math.max(...away)), ...(dir && !move.atEnd && along > finalDist * OVERSHOOT ? { overshoot: true } : {}) }, move.atEnd);
    }
    const turn = span(intervals((a, b) => angleDeg(a.rot, b.rot) > TURN_EPS_DEG));
    if (turn) push({ node, kind: 'turn', start: turn.start, end: turn.end, amount: round(Math.max(...series.map(s => angleDeg(s.rot, first.rot)))) }, turn.atEnd);
    const scale = span(intervals((a, b) => Math.abs(meanScale(b.scale, a.scale) - 1) > SCALE_EPS));
    if (scale) {
        const ratios = series.map(s => meanScale(s.scale, first.scale));
        const far = ratios.reduce((best, r) => Math.abs(r - 1) > Math.abs(best - 1) ? r : best, 1);
        const fin = ratios[last];
        const over = !scale.atEnd && Math.abs(far - 1) > Math.abs(fin - 1) * OVERSHOOT && Math.sign(far - 1) === Math.sign(fin - 1) && Math.abs(fin - 1) > 1e-3;
        push({ node, kind: 'scale', start: scale.start, end: scale.end, amount: round(far), ...(over ? { overshoot: true } : {}) }, scale.atEnd);
    }
    const fade = span(intervals((a, b) => Math.abs(a.opacity - b.opacity) > FADE_EPS));
    if (fade) {
        const net = series[last].opacity - first.opacity;
        const dev = series.map(s => s.opacity - first.opacity).reduce((best, d) => Math.abs(d) > Math.abs(best) ? d : best, 0);
        push({ node, kind: 'fade', start: fade.start, end: fade.end, amount: round(Math.abs(net) > FADE_EPS ? net : dev) }, fade.atEnd);
    }
    for (const kind of ['show', 'hide'] as const) {
        const i = series.slice(1).findIndex((s, j) => kind === 'show' ? !series[j].active && s.active : series[j].active && !s.active);
        if (i >= 0) rows.push({ node, kind, start: round((i + 1) * dt), end: round((i + 1) * dt), amount: 1 });
    }
    return { rows, continuous };
}

/** What happens in the first `windowS` seconds of play, sampled at `hz`. The source scene is not touched. */
export async function motionTable(trace: TraceScene, objects?: readonly SceneObject[], opts: MotionOptions = {}): Promise<MotionTable> {
    const windowS = opts.windowS ?? 3, hz = opts.hz ?? 10;
    if (!(windowS > 0) || !(hz > 0)) throw new Error('windowS and hz must be positive');
    const steps = Math.max(1, Math.round(windowS * hz)), dt = windowS / steps;
    const budget = makeBudget(opts.timeBudgetMs, 3000);
    const table = await withRun(trace, objects, budget, async run => {
        const all = run.trace.getSceneObjects().filter(o => !run.scope || run.scope.has(o.getPath()));
        const series = new Map<SceneObject, Sample[]>(all.map(o => [o, [sample(o)]]));
        for (let i = 0; i < steps; i++) {
            await run.advanceTo((i + 1) * dt * 1000);
            if (run.truncated) break;
            for (const o of all) series.get(o)!.push(sample(o));
        }
        const rows: MotionRow[] = [], continuous = new Set<MotionRow>();
        const byNode = new Map<string, MotionRow[]>();
        for (const o of all) {
            const found = rowsFor(o.getPath(), series.get(o)!, dt);
            rows.push(...found.rows);
            found.continuous.forEach(r => continuous.add(r));
            byNode.set(o.getPath(), found.rows);
        }
        rows.sort((a, b) => a.start - b.start || a.node.localeCompare(b.node));
        const settling = rows.filter(r => !continuous.has(r));
        const settledAt = settling.length ? Math.max(...settling.map(r => r.end)) : null;

        // Entrances: the same parent's children, ordered by when their motion (else their appearance) begins.
        const groups: MotionGroup[] = [];
        const children = new Map<string, { index: number; start: number }[]>();
        for (const o of all) {
            const mine = byNode.get(o.getPath()) ?? [];
            if (!mine.length) continue;
            const motion = mine.filter(r => r.kind !== 'show' && r.kind !== 'hide');
            const start = Math.min(...(motion.length ? motion : mine).map(r => r.start));
            const parent = o.getParent();
            const key = parent?.getPath() ?? '';
            const index = parent ? parent.getChildren().indexOf(o) : 0;
            (children.get(key) ?? children.set(key, []).get(key)!).push({ index, start });
        }
        for (const [parent, list] of children) {
            if (list.length < 2) continue;
            list.sort((a, b) => a.index - b.index);
            const starts = list.map(x => x.start);
            const gaps = starts.slice(1).map((s, i) => s - starts[i]);
            const distinct = [...starts].sort((a, b) => a - b).map((s, i, arr) => i ? s - arr[i - 1] : 0).filter(g => g > dt / 2);
            const order: MotionGroup['order'] = gaps.every(g => Math.abs(g) < dt / 2 + 1e-9) ? 'simultaneous'
                : gaps.every(g => g > -1e-9) ? 'sequential' : gaps.every(g => g < 1e-9) ? 'reverse' : 'mixed';
            groups.push({ parent, count: list.length, staggerS: distinct.length ? round(median(distinct)) : null, order });
        }
        groups.sort((a, b) => b.count - a.count || a.parent.localeCompare(b.parent));
        const out: MotionTable = { rows, settledAt, idle: rows.length === 0, groups };
        if (run.truncated) out.truncated = true;
        return out;
    });
    return table ?? { rows: [], settledAt: null, idle: true, groups: [], truncated: true };
}
